/**
 * Drop Update Wizard
 * ------------------
 * Shows a one-time, Win95-style "installing an update" window that announces a
 * product drop. Config is rendered server-side by sections/update-drop-app.liquid
 * into a <script type="application/json" id="st-update-wizard-data"> tag.
 *
 * Behaviour:
 *  - Waits until the desktop is ready (#desktop gains the `visible` class).
 *  - Shows once per visitor, keyed by Drop ID (localStorage). Bump the Drop ID
 *    in the theme editor to re-show the wizard to everyone.
 *  - Fully skippable; never blocks the desktop behind it.
 */
(function () {
    'use strict';

    var SEEN_PREFIX = 'smash_drop_seen_';
    var WINDOW_ID = 'update-wizard';

    function readConfig() {
        var el = document.getElementById('st-update-wizard-data');
        if (!el) return null;
        try {
            var cfg = JSON.parse(el.textContent);
            return (cfg && cfg.enabled && cfg.items && cfg.items.length) ? cfg : null;
        } catch (e) {
            return null;
        }
    }

    function seenKey(id) { return SEEN_PREFIX + (id || 'drop'); }
    function hasSeen(id) { try { return localStorage.getItem(seenKey(id)) === '1'; } catch (e) { return false; } }
    function markSeen(id) { try { localStorage.setItem(seenKey(id), '1'); } catch (e) { /* private mode */ } }

    function esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
        });
    }

    // "Court Mesh Tee" -> "COURT_MESH_TEE.dll"
    function filename(title, i) {
        var exts = ['dll', 'sys', 'exe', 'dat', 'drv', 'bin', 'sys'];
        var base = String(title || 'item').toUpperCase()
            .replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 22) || 'ITEM';
        return base + '.' + exts[i % exts.length];
    }

    // Deterministic pseudo file-size for texture (no Math.random — stable per index)
    function fakeSize(i) {
        var kb = 140 + ((i * 173) % 820);
        return (kb / 100).toFixed(1) + ' MB';
    }

    function whenDesktopReady(cb) {
        var desktop = document.getElementById('desktop');
        if (desktop && desktop.classList.contains('visible')) { cb(); return; }

        var fired = false;
        var obs = null;
        function fire() {
            if (fired) return;
            fired = true;
            if (obs) obs.disconnect();
            cb();
        }
        if (desktop && 'MutationObserver' in window) {
            obs = new MutationObserver(function () {
                if (desktop.classList.contains('visible')) fire();
            });
            obs.observe(desktop, { attributes: true, attributeFilter: ['class'] });
        }
        // Fallback poll + hard safety timeout in case the class is missed.
        var tries = 0;
        (function poll() {
            if (fired) return;
            var d = document.getElementById('desktop');
            if (d && d.classList.contains('visible')) { fire(); return; }
            if (++tries > 150) { fire(); return; } // ~15s ceiling
            setTimeout(poll, 100);
        })();
    }

    function buildContent(cfg) {
        var total = cfg.items.length;
        return [
            '<div class="st-uw">',
            '  <div class="st-uw__head">',
            '    <div class="st-uw__disk" aria-hidden="true">💾</div>',
            '    <div class="st-uw__headtext">',
            '      <div class="st-uw__h" data-uw-h>' + esc(cfg.heading) + '</div>',
            '      <div class="st-uw__sub" data-uw-sub>Adding new items to Smash OS. Please wait…</div>',
            '    </div>',
            '  </div>',
            '  <div class="st-uw__console" data-uw-console aria-live="polite"></div>',
            '  <div class="st-uw__statusrow">',
            '    <span class="st-uw__cur" data-uw-cur>Preparing…</span>',
            '    <span class="st-uw__count"><b data-uw-done>0</b> / <b>' + total + '</b></span>',
            '  </div>',
            '  <div class="st-uw__bar"><i class="st-uw__fill" data-uw-fill></i></div>',
            '  <div class="st-uw__note" data-uw-note>⚠ Do not power off your console.</div>',
            '  <div class="st-uw__actions">',
            '    <button type="button" class="st-uw__btn" data-uw-skip>Skip</button>',
            '    <button type="button" class="st-uw__btn st-uw__btn--go" data-uw-view hidden>' + esc(cfg.ctaLabel) + ' ▸</button>',
            '  </div>',
            '</div>'
        ].join('');
    }

    // Default windows (e.g. the hero banner) are held until the wizard is
    // dismissed, so the drop is the first thing the visitor sees.
    var defaultsReleased = false;
    function releaseDefaultWindows() {
        if (defaultsReleased) return;
        defaultsReleased = true;
        window.__smashDeferDefaultWindows = false;
        var wm = window.smashOS && window.smashOS.windowManager;
        if (wm && typeof wm.openDefaultWindows === 'function') {
            wm.openDefaultWindows();
        }
    }

    function closeWindow(el) {
        var wm = window.smashOS && window.smashOS.windowManager;
        if (wm && typeof wm.close === 'function') { wm.close(WINDOW_ID); return; }
        // Fallback: click the titlebar close button.
        var btn = el && el.querySelector('.st-titlebar__btn--close');
        if (btn) btn.click();
    }

    function openDrop(cfg) {
        var wm = window.smashOS && window.smashOS.windowManager;
        if (wm && typeof wm.openCollection === 'function' && cfg.collection) {
            wm.openCollection(cfg.collection, {});
        }
    }

    // Briefly pulse the matching collection desktop icon so the drop "lands"
    // somewhere on the desktop, like a freshly installed program.
    function pulseDropIcon(cfg) {
        if (!cfg.collection) return;
        var icon = document.querySelector('[data-action-type="collection"][data-action-value="' + cfg.collection + '"]')
            || document.querySelector('[data-action-value="' + cfg.collection + '"]');
        if (icon) {
            icon.classList.add('st-uw-pop');
            setTimeout(function () { icon.classList.remove('st-uw-pop'); }, 2400);
        }
    }

    function animate(el, cfg) {
        var body = el.querySelector('.st-window__body');
        if (!body) return;

        var consoleEl = body.querySelector('[data-uw-console]');
        var fill = body.querySelector('[data-uw-fill]');
        var cur = body.querySelector('[data-uw-cur]');
        var doneEl = body.querySelector('[data-uw-done]');
        var note = body.querySelector('[data-uw-note]');
        var hEl = body.querySelector('[data-uw-h]');
        var subEl = body.querySelector('[data-uw-sub]');
        var viewBtn = body.querySelector('[data-uw-view]');
        var skipBtn = body.querySelector('[data-uw-skip]');
        var statusText = el.querySelector('.st-statusbar__text');

        var items = cfg.items || [];
        var total = items.length;
        var done = 0;
        var finished = false;
        var timers = [];
        var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var stepMs = reduced ? 70 : 520;   // total time per item
        var okGap = reduced ? 35 : 230;    // delay before a line flips to "OK"

        function t(fn, ms) { var id = setTimeout(fn, ms); timers.push(id); return id; }
        function clearAll() { timers.forEach(clearTimeout); timers.length = 0; }

        function addLine(html, cls) {
            var line = document.createElement('div');
            line.className = 'st-uw__line' + (cls ? ' ' + cls : '');
            line.innerHTML = html;
            consoleEl.appendChild(line);
            consoleEl.scrollTop = consoleEl.scrollHeight;
            return line;
        }

        function setProgress() {
            var pct = total ? Math.round((done / total) * 100) : 100;
            if (fill) fill.style.width = pct + '%';
            if (doneEl) doneEl.textContent = String(done);
        }

        function finish() {
            if (finished) return;
            finished = true;
            clearAll();
            if (cfg.extra && cfg.extra > 0) {
                addLine('…and ' + cfg.extra + ' more item' + (cfg.extra > 1 ? 's' : '') + '.', 'st-uw__line--dim');
            }
            addLine('<b>Update complete.</b>', 'st-uw__line--ok');
            if (fill) { fill.style.width = '100%'; fill.classList.add('is-done'); }
            if (doneEl) doneEl.textContent = String(total);
            if (cur) cur.textContent = 'Done';
            var n = total + (cfg.extra || 0);
            if (hEl) hEl.textContent = 'Update complete';
            if (subEl) subEl.textContent = n + ' new item' + (n > 1 ? 's' : '') + ' installed and ready to shop.';
            if (note) { note.textContent = '✓ Ready. The drop is live.'; note.classList.add('is-ok'); }
            if (viewBtn) { viewBtn.hidden = false; viewBtn.classList.add('is-pulse'); }
            if (skipBtn) skipBtn.textContent = 'Close';
            if (statusText) statusText.textContent = 'Update complete';
            pulseDropIcon(cfg);
        }

        function runItem(i) {
            if (finished) return;
            if (i >= total) { t(finish, Math.round(okGap * 1.5)); return; }
            var fn = filename(items[i].title, i);
            if (cur) cur.textContent = 'Installing ' + fn;
            var line = addLine(
                'Installing <span class="st-uw__fn">' + esc(fn) + '</span>' +
                ' <span class="st-uw__dots">…</span>' +
                ' <span class="st-uw__sz">' + fakeSize(i) + '</span>',
                'st-uw__line--run'
            );
            t(function () {
                if (finished) return;
                line.classList.remove('st-uw__line--run');
                line.classList.add('st-uw__line--ok');
                var dots = line.querySelector('.st-uw__dots');
                if (dots) dots.textContent = '';
                var ok = document.createElement('span');
                ok.className = 'st-uw__okmark';
                ok.textContent = ' OK';
                line.appendChild(ok);
                done++;
                setProgress();
                t(function () { runItem(i + 1); }, Math.max(0, stepMs - okGap));
            }, okGap);
        }

        // Wire buttons. First "Skip" fast-forwards to the finished state (so the
        // shopper still gets the View Drop button); once finished it closes.
        if (skipBtn) {
            skipBtn.addEventListener('click', function () {
                if (!finished) { done = total; setProgress(); finish(); }
                else { closeWindow(el); }
            });
        }
        if (viewBtn) {
            viewBtn.addEventListener('click', function () { openDrop(cfg); closeWindow(el); });
        }

        // Stop all timers if the window is closed (X button) mid-install.
        if ('MutationObserver' in window) {
            var removalObs = new MutationObserver(function () {
                if (!document.body.contains(el)) {
                    clearAll();
                    removalObs.disconnect();
                    releaseDefaultWindows(); // let the hero banner etc. open now
                }
            });
            removalObs.observe(document.body, { childList: true, subtree: true });
        }

        setProgress();
        t(function () { runItem(0); }, reduced ? 60 : 450);
    }

    async function show(cfg) {
        var wm = window.smashOS && window.smashOS.windowManager;
        if (!wm || typeof wm.open !== 'function') { releaseDefaultWindows(); return; }

        // Mark as seen up front so a reload mid-animation won't re-trigger it.
        markSeen(cfg.dropId);

        var isMobile = window.innerWidth <= 767;
        var w = isMobile ? Math.min(320, window.innerWidth - 16) : 470;
        var h = isMobile ? 390 : 380;
        var left = Math.max(8, Math.round((window.innerWidth - w) / 2));
        var top = isMobile ? 70 : 110;

        var el = await wm.open(WINDOW_ID, {
            title: cfg.title || 'Smash Update',
            icon: cfg.icon || null,
            content: buildContent(cfg),
            canResize: false,
            canMaximize: false,
            showMenuBar: false,
            showStatusBar: true,
            statusText: 'Installing…',
            windowType: 'update',
            dataAttributes: {
                'data-width': w + 'px',
                'data-height': h + 'px',
                'data-left': left + 'px',
                'data-top': top + 'px'
            }
        });
        if (el) animate(el, cfg);
        else releaseDefaultWindows(); // window failed to open — don't strand the hero banner
    }

    function init() {
        var cfg = readConfig();
        if (!cfg) return;
        if (hasSeen(cfg.dropId)) return;
        // We will show the wizard: hold default windows (hero banner) so the
        // drop appears first. Released when the wizard is dismissed. This is set
        // now (before openDefaultWindows runs) so the hero banner never flashes.
        window.__smashDeferDefaultWindows = true;
        whenDesktopReady(function () {
            // Small beat after the desktop settles, then show the drop.
            setTimeout(function () { show(cfg); }, 700);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
